import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Tabs, 
  Tab, 
  Button,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField
} from '@mui/material';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import 'chartjs-adapter-dayjs-4';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { 
  FileDownload as DownloadIcon,
  FilterAlt as FilterIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import {
  collection,
  getDocs,
  limit as firestoreLimit,
  orderBy,
  query,
  Timestamp,
  where
} from 'firebase/firestore';
import { db, storage, functions } from '../../firebase/config';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { fetchDrivers } from '../../redux/slices/driversSlice';
import { fetchOperators } from '../../redux/slices/operatorsSlice';
import { isCentralOperationsRole, isOperatorRole } from '../../redux/slices/authSlice';
import { getPaymentCategory, getPaymentCategoryLabel, isAppPaidRide } from '../../utils/payment';
import PaymentChip from '../common/PaymentChip';
import {
  addWorkTimeBreak,
  deleteWorkTimeBreak,
  fetchWorkTimeRows,
  formatMinutes,
  summarizeWorkTimeRows,
  updateWorkTimeBreak,
  updateWorkTimeShift
} from '../../services/workTimeService';
import { jsPDF } from 'jspdf';

// Dayjs auf Deutsch einstellen
dayjs.locale('de');

// ChartJS registrieren
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Konstanten für die Datumsbereich-Filter
const FILTER_DAY = 'day';
const FILTER_WEEK = 'week';
const FILTER_MONTH = 'month';
const FILTER_LAST_MONTH = 'last_month';
const FILTER_QUARTER = 'quarter';
const FILTER_YEAR = 'year';
const FILTER_CUSTOM = 'custom';

// Tabs
const TAB_OVERVIEW = 0;
const TAB_RIDES = 1;
const TAB_DRIVERS = 2;
const TAB_WORK_TIMES = 3;
const TAB_REVENUE = 4;
const TAB_OPERATORS = 5;
const TAB_RECEIPTS = 6;
const CENTRAL_CREATED_OPERATOR_ID = '__central__';
const REPORT_STATUS_FILTER = [
  'open',
  'accepted',
  'in_progress',
  'arrived',
  'scheduled',
  'pre_accepted',
  'completed',
  'cancelled',
  'cancelled_by_driver',
  'archived'
];
const COMPLETED_REPORT_STATUS_FILTER = ['completed', 'archived'];
const REPORT_RIDES_LIMIT = 5000;

const CENTRAL_ROLES = new Set(['admin', 'super_admin', 'central_admin', 'dispatcher', 'central_dispatch']);
const NON_OPERATOR_CREATED_ROLES = new Set(['customer', 'driver', 'system']);

const getCreatedByCentralName = (ride) => {
  if (ride.createdByOperatorId || ride.preferredOperatorId) {
    return null;
  }

  if (CENTRAL_ROLES.has(ride.createdByRole)) {
    const userName = ride.createdByUserName || ride.createdByName || ride.createdByEmail || '';
    return userName ? `Hauptzentrale: ${userName}` : 'Hauptzentrale';
  }

  if (
    NON_OPERATOR_CREATED_ROLES.has(ride.createdByRole) ||
    ride.dispatchScope === 'central_pool' ||
    !ride.createdByRole
  ) {
    return 'Hauptzentrale';
  }

  return null;
};

// --- PayPal-App-Zahlungen (docs/paypal_app_zahlungen_konzept.md, 8.5) -------
// Zahlungsart-Ableitung jetzt zentral in ../../utils/payment (gemeinsame Quelle
// für Umsatzanalyse, RidesList und LiveMap). Import oben.

const getMediationLabel = (mediationType) => {
  switch (mediationType) {
    case 'cross_operator':
      return 'Vermittelt';
    case 'same_operator':
      return 'Eigen';
    case 'central':
      return 'Zentrale';
    default:
      return 'Unklar';
  }
};

const getDriverDisplayName = (driver) => (
  driver?.name ||
  driver?.displayName ||
  driver?.user?.name ||
  driver?.user?.displayName ||
  driver?.email ||
  driver?.user?.email ||
  driver?.id
);

const isDriverReportRecord = (driver) => driver?.isDriver !== false;

const toDateTimeInputValue = (date) => {
  if (!date) return '';
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
};

const toDateInputValue = (date) => {
  if (!date) return '';
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};

const createBreakFormFromBreak = (breakItem) => ({
  type: breakItem?.type || 'Pause',
  startTime: toDateTimeInputValue(breakItem?.startTime || new Date()),
  endTime: toDateTimeInputValue(breakItem?.endTime || new Date(Date.now() + 30 * 60000)),
  correctionReason: breakItem?.correctionReason || ''
});

const createShiftFormFromRow = (row) => ({
  shiftStart: toDateTimeInputValue(row?.shiftStart || new Date()),
  shiftEnd: toDateTimeInputValue(row?.displayShiftEnd || row?.shiftEnd || row?.effectiveEnd || new Date(Date.now() + 8 * 60 * 60000)),
  correctionReason: ''
});

const toReportTimestamp = (date) => Timestamp.fromDate(date instanceof Date ? date : new Date(date));

const normalizeReportRide = (rideDoc) => {
  const rideData = rideDoc.data();
  const processedRide = {
    id: rideDoc.id,
    ...rideData,
  };

  [
    'timestamp',
    'acceptedTimestamp',
    'acceptedAt',
    'arrivedTimestamp',
    'passengerPickedUpTimestamp',
    'completionTimestamp',
    'completedTimestamp',
    'scheduledTime',
    'startedAt'
  ].forEach((field) => {
    const value = rideData[field];
    if (value && typeof value.toDate === 'function') {
      processedRide[field] = value.toDate().toISOString();
    }
  });

  return processedRide;
};

const fetchReportRideRows = async ({
  currentUser,
  status,
  dateField,
  startDate,
  endDate,
  resultLimit = REPORT_RIDES_LIMIT
}) => {
  const constraints = [];
  const operatorScope = isOperatorRole(currentUser) ? currentUser?.operatorId : null;

  if (operatorScope) {
    constraints.push(where('visibleToOperatorIds', 'array-contains', operatorScope));
  }

  if (Array.isArray(status) && status.length > 0) {
    constraints.push(where('status', 'in', status));
  } else if (status) {
    constraints.push(where('status', '==', status));
  }

  constraints.push(
    where(dateField, '>=', toReportTimestamp(startDate)),
    where(dateField, '<=', toReportTimestamp(endDate)),
    orderBy(dateField, 'desc')
  );

  if (resultLimit) {
    constraints.push(firestoreLimit(resultLimit));
  }

  const snapshot = await getDocs(query(collection(db, 'rides'), ...constraints));
  return snapshot.docs.map(normalizeReportRide);
};

/**
 * ReportsPage-Komponente
 * 
 * Zeigt verschiedene Berichte und Visualisierungen für Fahrten, Fahrer und Umsätze an.
 */
const ReportsPage = () => {
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState(TAB_OVERVIEW);
  const [dateRange, setDateRange] = useState(FILTER_MONTH);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customRangeDialogOpen, setCustomRangeDialogOpen] = useState(false);
  const [customRangeDraftStart, setCustomRangeDraftStart] = useState('');
  const [customRangeDraftEnd, setCustomRangeDraftEnd] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('all');
  const [selectedCreatedOperatorId, setSelectedCreatedOperatorId] = useState('all');
  const [selectedDriverOperatorId, setSelectedDriverOperatorId] = useState('all');
  const [selectedMediationStatus, setSelectedMediationStatus] = useState('all');
  const [workTimeRows, setWorkTimeRows] = useState([]);
  const [workTimeLoading, setWorkTimeLoading] = useState(false);
  const [workTimeError, setWorkTimeError] = useState(null);
  const [selectedWorkTimeDriverId, setSelectedWorkTimeDriverId] = useState('all');
  const [selectedWorkTimeStatus, setSelectedWorkTimeStatus] = useState('all');
  const [selectedWorkTimeBreakType, setSelectedWorkTimeBreakType] = useState('all');
  const [selectedWorkTimeRow, setSelectedWorkTimeRow] = useState(null);
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [editingBreak, setEditingBreak] = useState(null);
  const [breakForm, setBreakForm] = useState(createBreakFormFromBreak());
  const [breakActionError, setBreakActionError] = useState(null);
  const [breakSaving, setBreakSaving] = useState(false);
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftForm, setShiftForm] = useState(createShiftFormFromRow());
  const [shiftActionError, setShiftActionError] = useState(null);
  const [shiftSaving, setShiftSaving] = useState(false);
  const [overviewRides, setOverviewRides] = useState([]);
  const [completedRides, setCompletedRides] = useState([]);
  const [reportRidesLoading, setReportRidesLoading] = useState(false);
  const [reportRidesError, setReportRidesError] = useState(null);
  // Quittungen-Tab (Phase 2): direkt aus der receipts-Collection geladen.
  const [receiptRows, setReceiptRows] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptsError, setReceiptsError] = useState(null);
  const [receiptStatusFilter, setReceiptStatusFilter] = useState('all');
  const [receiptOpeningId, setReceiptOpeningId] = useState(null);
  const [resendReceipt, setResendReceipt] = useState(null); // aktive Zeile im Dialog
  const [resendEmail, setResendEmail] = useState('');
  const [resendError, setResendError] = useState('');
  const [resendBusy, setResendBusy] = useState(false);

  // Daten aus dem Redux Store abfragen
  const currentUser = useSelector(state => state.auth.user);
  const { drivers, isLoading: driversLoading, error: driversError } = useSelector(state => state.drivers);
  const { operators = [], isLoading: operatorsLoading, error: operatorsError } = useSelector(state => state.operators || {});
  
  const isLoading = reportRidesLoading || driversLoading || operatorsLoading;
  const error = reportRidesError || driversError || operatorsError;
  
  // Startdatum für den gewählten Zeitraum – stabil memoized
  const getDateRangeStart = useCallback(() => {
    const now = new Date();
    switch (dateRange) {
      case FILTER_DAY:
        now.setHours(0, 0, 0, 0);
        return now;
      case FILTER_WEEK: {
        const dayOfWeek = now.getDay();
        const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        now.setDate(diff);
        now.setHours(0, 0, 0, 0);
        return now;
      }
      case FILTER_MONTH:
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
      case FILTER_LAST_MONTH:
        now.setMonth(now.getMonth() - 1);
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
      case FILTER_QUARTER: {
        const quarter = Math.floor(now.getMonth() / 3);
        now.setMonth(quarter * 3);
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
      }
      case FILTER_YEAR:
        now.setMonth(0);
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
      case FILTER_CUSTOM:
        return customStartDate
          ? new Date(`${customStartDate}T00:00:00`)
          : new Date(now.getFullYear(), now.getMonth(), 1);
      default:
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
    }
  }, [customStartDate, dateRange]);

  // Überschriftentitel – nur neu berechnet wenn dateRange sich ändert
  const dateRangeTitle = useMemo(() => {
    const startDate = getDateRangeStart();
    const options = { month: 'long', year: 'numeric' };
    switch (dateRange) {
      case FILTER_DAY:
        return startDate.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
      case FILTER_WEEK: {
        const endOfWeek = new Date(startDate);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        return `${startDate.toLocaleDateString('de-DE', { day: 'numeric' })} - ${endOfWeek.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      }
      case FILTER_MONTH:
        return startDate.toLocaleDateString('de-DE', options);
      case FILTER_LAST_MONTH:
        return `Letzter Monat: ${startDate.toLocaleDateString('de-DE', options)}`;
      case FILTER_QUARTER: {
        const quarter = Math.floor(startDate.getMonth() / 3) + 1;
        return `Q${quarter} ${startDate.getFullYear()}`;
      }
      case FILTER_YEAR:
        return `${startDate.getFullYear()}`;
      case FILTER_CUSTOM: {
        const endDate = customEndDate ? new Date(`${customEndDate}T23:59:59`) : startDate;
        return `${startDate.toLocaleDateString('de-DE')} - ${endDate.toLocaleDateString('de-DE')}`;
      }
      default:
        return startDate.toLocaleDateString('de-DE', options);
    }
  }, [customEndDate, dateRange, getDateRangeStart]);

  const reportBounds = useMemo(() => {
    const start = getDateRangeStart();
    const end = new Date();
    if (dateRange === FILTER_DAY) {
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === FILTER_WEEK) {
      end.setTime(start.getTime());
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === FILTER_MONTH) {
      end.setFullYear(start.getFullYear(), start.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === FILTER_LAST_MONTH) {
      end.setFullYear(start.getFullYear(), start.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === FILTER_QUARTER) {
      end.setTime(start.getTime());
      end.setMonth(end.getMonth() + 3);
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === FILTER_YEAR) {
      end.setFullYear(start.getFullYear(), 11, 31);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === FILTER_CUSTOM && customEndDate) {
      end.setTime(new Date(`${customEndDate}T23:59:59.999`).getTime());
    }
    return { start, end };
  }, [customEndDate, dateRange, getDateRangeStart]);

  // Daten laden
  useEffect(() => {
    if (!currentUser) return undefined;

    let cancelled = false;

    const loadData = async () => {
      setReportRidesLoading(true);
      setReportRidesError(null);

      try {
        const [overviewRows, completedRows] = await Promise.all([
          fetchReportRideRows({
            currentUser,
            status: REPORT_STATUS_FILTER,
            dateField: 'timestamp',
            startDate: reportBounds.start,
            endDate: reportBounds.end
          }),
          fetchReportRideRows({
            currentUser,
            status: COMPLETED_REPORT_STATUS_FILTER,
            dateField: 'completionTimestamp',
            startDate: reportBounds.start,
            endDate: reportBounds.end
          }),
          dispatch(fetchDrivers()),
          dispatch(fetchOperators())
        ]);

        if (cancelled) return;
        setOverviewRides(overviewRows);
        setCompletedRides(completedRows);
      } catch (error) {
        console.error('Fehler beim Laden der Berichtsdaten:', error);
        if (!cancelled) {
          setReportRidesError(error.message || 'Berichtsdaten konnten nicht geladen werden.');
          setOverviewRides([]);
          setCompletedRides([]);
        }
      } finally {
        if (!cancelled) {
          setReportRidesLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [currentUser, dispatch, reportBounds]);

  const handleTabChange = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const handleFilterClick = useCallback((event) => {
    setFilterAnchorEl(event.currentTarget);
  }, []);

  const handleFilterClose = useCallback(() => {
    setFilterAnchorEl(null);
  }, []);

  const handleFilterSelect = useCallback((range) => {
    if (range === FILTER_CUSTOM) {
      const currentStart = customStartDate || toDateInputValue(reportBounds.start);
      const currentEnd = customEndDate || toDateInputValue(reportBounds.end);
      setCustomRangeDraftStart(currentStart);
      setCustomRangeDraftEnd(currentEnd);
      setCustomRangeDialogOpen(true);
      setFilterAnchorEl(null);
      return;
    }
    setDateRange(range);
    setFilterAnchorEl(null);
  }, [customEndDate, customStartDate, reportBounds]);

  const customRangeDraftInvalid = useMemo(() => {
    if (!customRangeDraftStart || !customRangeDraftEnd) {
      return true;
    }
    return new Date(`${customRangeDraftStart}T00:00:00`) > new Date(`${customRangeDraftEnd}T23:59:59`);
  }, [customRangeDraftEnd, customRangeDraftStart]);

  const handleCloseCustomRangeDialog = useCallback(() => {
    setCustomRangeDialogOpen(false);
  }, []);

  const handleApplyCustomRange = useCallback(() => {
    if (customRangeDraftInvalid) return;
    setCustomStartDate(customRangeDraftStart);
    setCustomEndDate(customRangeDraftEnd);
    setDateRange(FILTER_CUSTOM);
    setCustomRangeDialogOpen(false);
  }, [customRangeDraftEnd, customRangeDraftInvalid, customRangeDraftStart]);

  const reloadWorkTimes = useCallback(async () => {
    setWorkTimeLoading(true);
    setWorkTimeError(null);

    try {
      const rows = await fetchWorkTimeRows({
        startDate: reportBounds.start,
        endDate: reportBounds.end
      });
      setWorkTimeRows(rows);
      return rows;
    } catch (error) {
      console.error('Fehler beim Laden der Arbeitszeiten:', error);
      setWorkTimeError(error.message || 'Arbeitszeiten konnten nicht geladen werden.');
      setWorkTimeRows([]);
      return [];
    } finally {
      setWorkTimeLoading(false);
    }
  }, [reportBounds]);

  useEffect(() => {
    if (currentTab !== TAB_WORK_TIMES) return undefined;
    let cancelled = false;
    reloadWorkTimes().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [currentTab, reloadWorkTimes]);

  // Quittungen im Zeitraum laden (Range + orderBy auf issuedAt → kein
  // Composite-Index nötig). Nur beim aktiven Quittungen-Tab.
  const reloadReceipts = useCallback(async () => {
    setReceiptsLoading(true);
    setReceiptsError(null);
    try {
      const snapshot = await getDocs(query(
        collection(db, 'receipts'),
        where('issuedAt', '>=', Timestamp.fromDate(reportBounds.start)),
        where('issuedAt', '<=', Timestamp.fromDate(reportBounds.end)),
        orderBy('issuedAt', 'desc'),
        firestoreLimit(1000)
      ));
      const rows = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setReceiptRows(rows);
      return rows;
    } catch (error) {
      console.error('Fehler beim Laden der Quittungen:', error);
      setReceiptsError(error.message || 'Quittungen konnten nicht geladen werden.');
      setReceiptRows([]);
      return [];
    } finally {
      setReceiptsLoading(false);
    }
  }, [reportBounds]);

  useEffect(() => {
    if (currentTab !== TAB_RECEIPTS) return undefined;
    let cancelled = false;
    reloadReceipts().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [currentTab, reloadReceipts]);

  const toDate = useCallback((value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }, []);

  const getRideReferenceDate = useCallback((ride) => (
    toDate(ride.completedTimestamp) ||
    toDate(ride.completionTimestamp) ||
    toDate(ride.passengerPickedUpTimestamp) ||
    toDate(ride.timestamp)
  ), [toDate]);

  const formatDateTime = useCallback((value) => {
    const date = toDate(value);
    return date ? date.toLocaleString('de-DE') : '-';
  }, [toDate]);

  const formatMoney = useCallback((value) => {
    if (value === null || value === undefined || value === '') return '-';
    return `${Number(value).toFixed(2)} €`;
  }, []);

  // --- Quittungen-Tab (Phase 2) ------------------------------------------
  const receiptEmailStatusMeta = useCallback((status) => {
    switch (status) {
      case 'sent': return { label: 'versendet', color: 'success' };
      case 'failed': return { label: 'fehlgeschlagen', color: 'error' };
      case 'queued': return { label: 'in Zustellung', color: 'warning' };
      case 'no_address': return { label: 'keine Adresse', color: 'default' };
      case 'opted_out': return { label: 'abgemeldet', color: 'default' };
      case 'not_configured': return { label: 'nur In-App', color: 'default' };
      default: return { label: status || '—', color: 'default' };
    }
  }, []);

  const filteredReceiptRows = useMemo(() => {
    if (receiptStatusFilter === 'all') return receiptRows;
    return receiptRows.filter(r => (r.email?.status || '') === receiptStatusFilter);
  }, [receiptRows, receiptStatusFilter]);

  const receiptSummary = useMemo(() => {
    const s = { total: receiptRows.length, sent: 0, failed: 0, pending: 0, noEmail: 0, sum: 0 };
    for (const r of receiptRows) {
      const st = r.email?.status || '';
      if (st === 'sent') s.sent += 1;
      else if (st === 'failed') s.failed += 1;
      else if (st === 'queued') s.pending += 1;
      else s.noEmail += 1;
      s.sum += Number(r.amounts?.payableEur || 0);
    }
    return s;
  }, [receiptRows]);

  const handleOpenReceiptPdf = useCallback(async (receipt) => {
    if (!receipt?.pdfPath) return;
    setReceiptOpeningId(receipt.id);
    try {
      const url = await getDownloadURL(storageRef(storage, receipt.pdfPath));
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      console.error('Quittungs-PDF konnte nicht geladen werden:', error);
    } finally {
      setReceiptOpeningId(null);
    }
  }, []);

  const handleOpenResend = useCallback((receipt) => {
    setResendError('');
    const to = String(receipt?.email?.to || '');
    setResendEmail(to.endsWith('@telephone.local') ? '' : to);
    setResendReceipt(receipt);
  }, []);

  const handleSubmitResend = useCallback(async () => {
    const email = resendEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setResendError('Bitte eine gültige E-Mail-Adresse eingeben');
      return;
    }
    setResendBusy(true);
    try {
      const resend = httpsCallable(functions, 'resendReceiptEmail');
      await resend({ rideId: resendReceipt.rideId || resendReceipt.id, to: email });
      setResendReceipt(null);
      await reloadReceipts();
    } catch (error) {
      console.error('Nachversand fehlgeschlagen:', error);
      setResendError(error?.message || 'Versand fehlgeschlagen');
    } finally {
      setResendBusy(false);
    }
  }, [resendEmail, resendReceipt, reloadReceipts]);

  const getBillableFare = useCallback((ride) => {
    // App-Zahlungen: Capture-Betrag ist die maßgebliche Umsatzgröße (8.5).
    if (isAppPaidRide(ride) && Number(ride.paymentCapturedAmountCents) > 0) {
      return Number(ride.paymentCapturedAmountCents) / 100;
    }
    const actual = ride.fare;
    const estimated = ride.estimatedEarningEur;
    return actual !== null && actual !== undefined ? Number(actual) :
      estimated !== null && estimated !== undefined ? Number(estimated) : 0;
  }, []);

  const getRideDiscountEur = useCallback((ride) => {
    // Eingelöster Treuerabatt (Treuekarte ODER Treuemeilen) für Fahrzeug-
    // Zahlungen. Bei App-Zahlung wird der kassierte Betrag stattdessen direkt
    // aus dem Capture (fare − Rabatt) abgeleitet (revenueReportRows).
    if (ride.loyaltyDiscountApplied === true && Number(ride.loyaltyDiscountEur) > 0) {
      return Number(ride.loyaltyDiscountEur);
    }
    if (ride.milesDiscountApplied === true && Number(ride.milesDiscountEur) > 0) {
      return Number(ride.milesDiscountEur);
    }
    return 0;
  }, []);

  const completedReportRides = useMemo(() => {
    const { start, end } = reportBounds;
    return completedRides.filter((ride) => {
      if (ride.status !== 'completed' && ride.status !== 'archived') return false;
      const refDate = getRideReferenceDate(ride);
      return refDate && refDate >= start && refDate <= end;
    });
  }, [completedRides, reportBounds, getRideReferenceDate]);

  const operatorNameById = useMemo(() => {
    return new Map(operators.map(operator => [operator.id, operator.name || operator.id]));
  }, [operators]);

  const driverReportRows = useMemo(() => {
    return completedReportRides
      .filter(ride => selectedDriverId === 'all' || ride.driverId === selectedDriverId)
      .map(ride => ({
        ...ride,
        reportType: ride.rideSource === 'driver_walk_in' || ride.walkInRide ? 'Einsteiger-Tour' : 'App-/Portal-Fahrt',
        refDate: getRideReferenceDate(ride),
        billableFare: getBillableFare(ride),
        fareSource: ride.fare !== null && ride.fare !== undefined ? 'Tatsächlich' :
          ride.estimatedEarningEur !== null && ride.estimatedEarningEur !== undefined ? 'Geschätzt' : 'Fehlt'
      }))
      .sort((a, b) => (b.refDate?.getTime() || 0) - (a.refDate?.getTime() || 0));
  }, [completedReportRides, selectedDriverId, getRideReferenceDate, getBillableFare]);

  const operatorReportRows = useMemo(() => {
    return completedReportRides
      .map(ride => {
        const centralCreatedByName = getCreatedByCentralName(ride);
        const createdOperatorId = ride.createdByOperatorId || ride.preferredOperatorId || (centralCreatedByName ? CENTRAL_CREATED_OPERATOR_ID : '');
        const rideHasAssignedDriverOrVehicle = Boolean(ride.driverId || ride.driverVehicleId || ride.driverName || ride.driverLicensePlate);
        const driverOperatorId = ride.driverOperatorId || ride.vehicleOperatorId || (rideHasAssignedDriverOrVehicle ? CENTRAL_CREATED_OPERATOR_ID : '');
        const driverOperatorName = driverOperatorId === CENTRAL_CREATED_OPERATOR_ID
          ? 'Hauptzentrale'
          : ride.driverOperatorName || operatorNameById.get(driverOperatorId) || driverOperatorId || 'Nicht zugeordnet';
        const mediationType = createdOperatorId === CENTRAL_CREATED_OPERATOR_ID
          ? 'central'
          : !createdOperatorId || !driverOperatorId
          ? 'unknown'
          : createdOperatorId === driverOperatorId
            ? 'same_operator'
            : 'cross_operator';
        // Zahlungsdimension für die Auszahlungsübersicht (Konzept 8.7):
        // App-Zahlungen laufen immer über das Hauptkonto — fährt ein
        // Sub-Unternehmer, entsteht eine interne Auszahlung.
        const paymentCategory = getPaymentCategory(ride);
        return {
          ...ride,
          createdOperatorId,
          createdOperatorName: ride.createdByOperatorName || ride.preferredOperatorName || centralCreatedByName || createdOperatorId || 'Zentrale / unbekannt',
          driverOperatorId,
          driverOperatorName,
          mediationType,
          refDate: getRideReferenceDate(ride),
          billableFare: getBillableFare(ride),
          paymentCategory,
          refundedEur: Number(ride.paymentRefundedAmountCents || 0) / 100,
          fareSource: ride.fare !== null && ride.fare !== undefined ? 'Tatsächlich' :
            ride.estimatedEarningEur !== null && ride.estimatedEarningEur !== undefined ? 'Geschätzt' : 'Fehlt'
        };
      })
      .filter(ride => selectedCreatedOperatorId === 'all' || ride.createdOperatorId === selectedCreatedOperatorId)
      .filter(ride => selectedDriverOperatorId === 'all' || ride.driverOperatorId === selectedDriverOperatorId)
      .filter(ride => selectedMediationStatus === 'all' || ride.mediationType === selectedMediationStatus)
      .sort((a, b) => (b.refDate?.getTime() || 0) - (a.refDate?.getTime() || 0));
  }, [
    completedReportRides,
    operatorNameById,
    selectedCreatedOperatorId,
    selectedDriverOperatorId,
    selectedMediationStatus,
    getRideReferenceDate,
    getBillableFare
  ]);

  // Auszahlungs-Referenzdaten je Unternehmer (am operators-Dokument gepflegt)
  const operatorPayoutInfoById = useMemo(() => {
    return new Map(operators.map(operator => [operator.id, {
      method: operator.payoutMethod || 'none',
      paypalEmail: operator.payoutPaypalEmail || '',
      iban: operator.payoutIban || '',
      accountHolder: operator.payoutAccountHolder || ''
    }]));
  }, [operators]);

  // Auszahlungsübersicht je ausführendem Sub-Unternehmer (Konzept 8.7):
  // App-Zahlungen gehen immer ans Hauptkonto — fährt ein Sub-Unternehmer,
  // ist der eingegangene Bruttobetrag (abzgl. Erstattungen) auszuzahlen.
  const operatorPayoutRows = useMemo(() => {
    const byOperator = new Map();
    for (const ride of operatorReportRows) {
      // Eigenfahrten der Zentrale lösen keine Auszahlung aus.
      if (!ride.driverOperatorId || ride.driverOperatorId === CENTRAL_CREATED_OPERATOR_ID) continue;
      if (!byOperator.has(ride.driverOperatorId)) {
        byOperator.set(ride.driverOperatorId, {
          operatorId: ride.driverOperatorId,
          operatorName: ride.driverOperatorName,
          appPaidCount: 0,
          appPaidGross: 0,
          appPaidRefunded: 0,
          inVehicleGross: 0
        });
      }
      const entry = byOperator.get(ride.driverOperatorId);
      if (ride.paymentCategory === 'app_paid') {
        entry.appPaidCount += 1;
        entry.appPaidGross += ride.billableFare;
        entry.appPaidRefunded += ride.refundedEur;
      } else {
        entry.inVehicleGross += ride.billableFare;
      }
    }
    return Array.from(byOperator.values())
      .map(entry => {
        const info = operatorPayoutInfoById.get(entry.operatorId) || { method: 'none' };
        const payoutTarget = info.method === 'paypal'
          ? (info.paypalEmail || 'PayPal (Adresse fehlt)')
          : info.method === 'bank'
            ? (info.iban || 'Bank (IBAN fehlt)')
            : 'nicht hinterlegt';
        return {
          ...entry,
          payoutMethod: info.method,
          payoutTarget,
          // Brutto-Auszahlungsbasis: am Hauptkonto eingegangen, abzüglich
          // bereits erstatteter Beträge. Provisionen/Abzüge sind Sache der
          // Zentrale und werden hier bewusst nicht verrechnet.
          payoutDue: Math.max(0, entry.appPaidGross - entry.appPaidRefunded)
        };
      })
      .filter(entry => entry.appPaidCount > 0)
      .sort((a, b) => b.payoutDue - a.payoutDue);
  }, [operatorReportRows, operatorPayoutInfoById]);

  // Umsatzanalyse-Zeilen: abgeschlossene Fahrten mit Zahlungsart-Dimension (8.5)
  const revenueReportRows = useMemo(() => {
    return completedReportRides
      .filter(ride => selectedDriverId === 'all' || ride.driverId === selectedDriverId)
      .map(ride => {
        // Brutto = voller Fahrpreis (`fare`, auch bei App-Zahlung). Kassiert =
        // bei App-Zahlung der eingezogene Capture (fare − Rabatt seit Phase 2),
        // sonst fare − Treuerabatt. Rabatt konsistent als Differenz abgeleitet.
        const grossFare = (ride.fare !== null && ride.fare !== undefined)
          ? Number(ride.fare) : getBillableFare(ride);
        const captured = Number(ride.paymentCapturedAmountCents || 0) / 100;
        const collectedFare = (isAppPaidRide(ride) && captured > 0)
          ? captured : grossFare - getRideDiscountEur(ride);
        return {
          ...ride,
          refDate: getRideReferenceDate(ride),
          billableFare: grossFare,
          discountEur: Math.max(0, grossFare - collectedFare),
          collectedFare,
          paymentCategory: getPaymentCategory(ride),
          refundedEur: Number(ride.paymentRefundedAmountCents || 0) / 100
        };
      })
      .sort((a, b) => (b.refDate?.getTime() || 0) - (a.refDate?.getTime() || 0));
  }, [completedReportRides, selectedDriverId, getRideReferenceDate, getBillableFare, getRideDiscountEur]);

  const revenueSummary = useMemo(() => {
    // Umsatz = tatsächlich KASSIERTE Beträge (fare − Treuerabatt, Konzept 3.1),
    // damit die Summe der Kasse entspricht. `gross` = voller Fahrpreis vor
    // Rabatt bleibt separat sichtbar (KPI „Fahrpreis brutto").
    const summary = {
      total: 0,
      gross: 0,
      discount: 0,
      discountCount: 0,
      appPaid: 0,
      inVehicle: 0,
      appPaidCount: 0,
      failedCount: 0,
      toCashCount: 0,
      pendingCount: 0,
      refunded: 0
    };
    for (const ride of revenueReportRows) {
      summary.total += ride.collectedFare;
      summary.gross += ride.billableFare;
      summary.discount += ride.discountEur;
      if (ride.discountEur > 0) summary.discountCount += 1;
      if (ride.paymentCategory === 'app_paid') {
        summary.appPaid += ride.collectedFare;
        summary.appPaidCount += 1;
      } else {
        summary.inVehicle += ride.collectedFare;
      }
      if (ride.paymentCategory === 'app_failed') summary.failedCount += 1;
      if (ride.paymentCategory === 'app_to_cash') summary.toCashCount += 1;
      if (ride.paymentCategory === 'app_pending') summary.pendingCount += 1;
      summary.refunded += ride.refundedEur;
    }
    return summary;
  }, [revenueReportRows]);

  const driverNameById = useMemo(() => {
    return new Map(drivers.map(driver => [
      driver.id,
      getDriverDisplayName(driver)
    ]));
  }, [drivers]);

  const workTimeDriverOptions = useMemo(() => {
    return drivers
      .filter(isDriverReportRecord)
      .filter(driver => {
        if (isOperatorRole(currentUser)) {
          return driver.operatorId === currentUser?.operatorId;
        }
        if (isCentralOperationsRole(currentUser)) {
          return !driver.operatorId;
        }
        return false;
      })
      .sort((a, b) => (getDriverDisplayName(a) || '').localeCompare(getDriverDisplayName(b) || '', 'de'));
  }, [drivers, currentUser]);

  const workTimeDriverIdsInScope = useMemo(() => (
    new Set(workTimeDriverOptions.map(driver => driver.id))
  ), [workTimeDriverOptions]);

  const workTimeBreakTypes = useMemo(() => {
    return Array.from(new Set(
      workTimeRows.flatMap(row => row.breakTypes || []).filter(Boolean)
    )).sort((a, b) => a.localeCompare(b, 'de'));
  }, [workTimeRows]);

  const filteredWorkTimeRows = useMemo(() => {
    return workTimeRows
      .map(row => ({
        ...row,
        displayDriverName: driverNameById.get(row.driverId) || row.driverName || row.driverId
      }))
      .filter(row => {
        if (workTimeDriverIdsInScope.has(row.driverId)) return true;
        if (isOperatorRole(currentUser)) return row.operatorId === currentUser?.operatorId;
        if (isCentralOperationsRole(currentUser)) return !row.operatorId;
        return false;
      })
      .filter(row => selectedWorkTimeDriverId === 'all' || row.driverId === selectedWorkTimeDriverId)
      .filter(row => {
        if (selectedWorkTimeStatus === 'all') return true;
        if (selectedWorkTimeStatus === 'active') return row.isActive;
        if (selectedWorkTimeStatus === 'completed') return !row.isActive;
        if (selectedWorkTimeStatus === 'overtime') return row.overtimeMinutes > 0;
        return true;
      })
      .filter(row => (
        selectedWorkTimeBreakType === 'all' ||
        (row.breakTypes || []).includes(selectedWorkTimeBreakType)
      ));
  }, [
    workTimeRows,
    driverNameById,
    workTimeDriverIdsInScope,
    currentUser,
    selectedWorkTimeDriverId,
    selectedWorkTimeStatus,
    selectedWorkTimeBreakType
  ]);

  const workTimeSummary = useMemo(
    () => summarizeWorkTimeRows(filteredWorkTimeRows),
    [filteredWorkTimeRows]
  );

  const withWorkTimeDisplayName = useCallback((row) => row ? ({
    ...row,
    displayDriverName: driverNameById.get(row.driverId) || row.driverName || row.driverId
  }) : null, [driverNameById]);

  const refreshSelectedWorkTimeRow = useCallback(async (rowId) => {
    const rows = await reloadWorkTimes();
    const updatedRow = rows.find(row => row.id === rowId);
    setSelectedWorkTimeRow(withWorkTimeDisplayName(updatedRow));
  }, [reloadWorkTimes, withWorkTimeDisplayName]);

  const handleOpenWorkTimeDetails = useCallback((row) => {
    setSelectedWorkTimeRow(row);
    setBreakActionError(null);
  }, []);

  const handleCloseWorkTimeDetails = useCallback(() => {
    setSelectedWorkTimeRow(null);
    setBreakActionError(null);
    setShiftActionError(null);
  }, []);

  const handleOpenShiftDialog = useCallback((row) => {
    setSelectedWorkTimeRow(row);
    setShiftForm(createShiftFormFromRow(row));
    setShiftActionError(null);
    setShiftDialogOpen(true);
  }, []);

  const handleCloseShiftDialog = useCallback(() => {
    setShiftDialogOpen(false);
    setShiftActionError(null);
  }, []);

  const handleShiftFormChange = useCallback((field, value) => {
    setShiftForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveShift = useCallback(async () => {
    if (!selectedWorkTimeRow) return;
    if (!shiftForm.correctionReason.trim()) {
      setShiftActionError('Bitte einen Grund für die Änderung angeben.');
      return;
    }

    setShiftSaving(true);
    setShiftActionError(null);

    try {
      await updateWorkTimeShift({
        dateKey: selectedWorkTimeRow.dateKey,
        driverId: selectedWorkTimeRow.driverId,
        shiftNumber: selectedWorkTimeRow.shiftNumber,
        shiftIndex: selectedWorkTimeRow.shiftIndex,
        shiftData: {
          shiftStart: new Date(shiftForm.shiftStart),
          shiftEnd: new Date(shiftForm.shiftEnd),
          correctionReason: shiftForm.correctionReason.trim(),
          source: 'web_admin'
        }
      });
      setShiftDialogOpen(false);
      await refreshSelectedWorkTimeRow(selectedWorkTimeRow.id);
    } catch (error) {
      setShiftActionError(error.message || 'Schichtzeiten konnten nicht gespeichert werden.');
    } finally {
      setShiftSaving(false);
    }
  }, [refreshSelectedWorkTimeRow, selectedWorkTimeRow, shiftForm]);

  const handleOpenAddBreak = useCallback((row) => {
    const startTime = row.isActive ? new Date() : row.shiftStart;
    const endCandidate = new Date(startTime.getTime() + 30 * 60000);
    const endTime = row.effectiveEnd && endCandidate > row.effectiveEnd ? row.effectiveEnd : endCandidate;

    setSelectedWorkTimeRow(row);
    setEditingBreak(null);
    setBreakForm({
      type: 'Pause',
      startTime: toDateTimeInputValue(startTime),
      endTime: toDateTimeInputValue(endTime),
      correctionReason: ''
    });
    setBreakActionError(null);
    setBreakDialogOpen(true);
  }, []);

  const handleOpenEditBreak = useCallback((breakItem) => {
    setEditingBreak(breakItem);
    setBreakForm(createBreakFormFromBreak(breakItem));
    setBreakActionError(null);
    setBreakDialogOpen(true);
  }, []);

  const handleCloseBreakDialog = useCallback(() => {
    setBreakDialogOpen(false);
    setEditingBreak(null);
    setBreakActionError(null);
  }, []);

  const handleBreakFormChange = useCallback((field, value) => {
    setBreakForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveBreak = useCallback(async () => {
    if (!selectedWorkTimeRow) return;
    if (!breakForm.correctionReason.trim()) {
      setBreakActionError('Bitte einen Grund für die Änderung angeben.');
      return;
    }

    setBreakSaving(true);
    setBreakActionError(null);

    const payload = {
      dateKey: selectedWorkTimeRow.dateKey,
      driverId: selectedWorkTimeRow.driverId,
      shiftNumber: selectedWorkTimeRow.shiftNumber,
      shiftIndex: selectedWorkTimeRow.shiftIndex,
      breakData: {
        type: breakForm.type,
        startTime: new Date(breakForm.startTime),
        endTime: new Date(breakForm.endTime),
        correctionReason: breakForm.correctionReason.trim(),
        source: 'web_admin'
      }
    };

    try {
      if (editingBreak) {
        await updateWorkTimeBreak({
          ...payload,
          breakIndex: editingBreak.breakIndex
        });
      } else {
        await addWorkTimeBreak(payload);
      }
      setBreakDialogOpen(false);
      setEditingBreak(null);
      await refreshSelectedWorkTimeRow(selectedWorkTimeRow.id);
    } catch (error) {
      setBreakActionError(error.message || 'Pause konnte nicht gespeichert werden.');
    } finally {
      setBreakSaving(false);
    }
  }, [breakForm, editingBreak, refreshSelectedWorkTimeRow, selectedWorkTimeRow]);

  const handleDeleteBreak = useCallback(async (breakItem) => {
    if (!selectedWorkTimeRow || !breakItem) return;
    const confirmed = window.confirm(`Pause "${breakItem.type}" wirklich löschen?`);
    if (!confirmed) return;

    setBreakSaving(true);
    setBreakActionError(null);

    try {
      await deleteWorkTimeBreak({
        dateKey: selectedWorkTimeRow.dateKey,
        driverId: selectedWorkTimeRow.driverId,
        shiftNumber: selectedWorkTimeRow.shiftNumber,
        shiftIndex: selectedWorkTimeRow.shiftIndex,
        breakIndex: breakItem.breakIndex
      });
      await refreshSelectedWorkTimeRow(selectedWorkTimeRow.id);
    } catch (error) {
      setBreakActionError(error.message || 'Pause konnte nicht gelöscht werden.');
    } finally {
      setBreakSaving(false);
    }
  }, [refreshSelectedWorkTimeRow, selectedWorkTimeRow]);

  const renderWorkTimeDialogs = () => (
    <>
      <Dialog
        open={Boolean(selectedWorkTimeRow)}
        onClose={handleCloseWorkTimeDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Schichtdetails
        </DialogTitle>
        <DialogContent dividers>
          {selectedWorkTimeRow && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Fahrer</Typography>
                  <Typography variant="body1">{selectedWorkTimeRow.displayDriverName}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Datum / Schicht</Typography>
                  <Typography variant="body1">{selectedWorkTimeRow.dateKey} / #{selectedWorkTimeRow.shiftNumber}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body1">{selectedWorkTimeRow.isActive ? 'Aktiv' : 'Abgeschlossen'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Start</Typography>
                  <Typography variant="body1">{formatDateTime(selectedWorkTimeRow.shiftStart)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Ende</Typography>
                  <Typography variant="body1">
                    {selectedWorkTimeRow.isActive ? 'läuft' : formatDateTime(selectedWorkTimeRow.displayShiftEnd || selectedWorkTimeRow.shiftEnd)}
                  </Typography>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={2.4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Online-Brutto</Typography>
                    <Typography variant="body1" fontWeight={700}>{formatMinutes(selectedWorkTimeRow.grossMinutes)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Pausen abgezogen</Typography>
                    <Typography variant="body1" fontWeight={700}>{formatMinutes(selectedWorkTimeRow.breakMinutes)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Nicht abgezogen</Typography>
                    <Typography variant="body1" fontWeight={700}>{formatMinutes(selectedWorkTimeRow.nonDeductibleBreakMinutes || 0)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Netto</Typography>
                    <Typography variant="body1" fontWeight={700}>{formatMinutes(selectedWorkTimeRow.netMinutes)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Überstunden</Typography>
                    <Typography variant="body1" fontWeight={700}>{formatMinutes(selectedWorkTimeRow.overtimeMinutes)}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {(breakActionError || shiftActionError) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {breakActionError || shiftActionError}
                </Alert>
              )}

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Schichtzeiten</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenShiftDialog(selectedWorkTimeRow)}
                >
                  Zeiten korrigieren
                </Button>
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Pausen</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenAddBreak(selectedWorkTimeRow)}
                >
                  Pause hinzufügen
                </Button>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Typ</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell>Ende</TableCell>
                      <TableCell align="right">Dauer</TableCell>
                      <TableCell>Wertung</TableCell>
                      <TableCell>Quelle</TableCell>
                      <TableCell>Grund</TableCell>
                      <TableCell align="right">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedWorkTimeRow.breaks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">Keine Pausen erfasst</TableCell>
                      </TableRow>
                    ) : (
                      selectedWorkTimeRow.breaks.map(breakItem => (
                        <TableRow key={breakItem.id}>
                          <TableCell>{breakItem.type}</TableCell>
                          <TableCell>{formatDateTime(breakItem.startTime)}</TableCell>
                          <TableCell>{formatDateTime(breakItem.endTime)}</TableCell>
                          <TableCell align="right">{formatMinutes(breakItem.minutes)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={breakItem.isDeductible ? 'Abgezogen' : 'Nicht abgezogen'}
                              color={breakItem.isDeductible ? 'default' : 'info'}
                              variant={breakItem.isDeductible ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell>{breakItem.source}</TableCell>
                          <TableCell>{breakItem.correctionReason || '-'}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              title="Pause bearbeiten"
                              onClick={() => handleOpenEditBreak(breakItem)}
                              disabled={breakSaving}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Pause löschen"
                              onClick={() => handleDeleteBreak(breakItem)}
                              disabled={breakSaving}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWorkTimeDetails}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={shiftDialogOpen}
        onClose={handleCloseShiftDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schichtzeiten korrigieren</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Das Speichern setzt ein Schichtende und markiert die Schicht als abgeschlossen.
          </DialogContentText>
          {shiftActionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {shiftActionError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Schichtbeginn"
              type="datetime-local"
              value={shiftForm.shiftStart}
              onChange={(event) => handleShiftFormChange('shiftStart', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Schichtende"
              type="datetime-local"
              value={shiftForm.shiftEnd}
              onChange={(event) => handleShiftFormChange('shiftEnd', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Grund der Änderung"
              value={shiftForm.correctionReason}
              onChange={(event) => handleShiftFormChange('correctionReason', event.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShiftDialog} disabled={shiftSaving}>Abbrechen</Button>
          <Button onClick={handleSaveShift} variant="contained" disabled={shiftSaving}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={breakDialogOpen}
        onClose={handleCloseBreakDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingBreak ? 'Pause bearbeiten' : 'Pause hinzufügen'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Änderungen werden direkt in der Schicht gespeichert und danach neu berechnet.
          </DialogContentText>
          {breakActionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {breakActionError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Pausentyp</InputLabel>
              <Select
                label="Pausentyp"
                value={breakForm.type}
                onChange={(event) => handleBreakFormChange('type', event.target.value)}
              >
                <MenuItem value="Pause">Pause</MenuItem>
                <MenuItem value="Stadtfahrt">Stadtfahrt</MenuItem>
                <MenuItem value="Langstrecke">Langstrecke</MenuItem>
                <MenuItem value="Disposition">Disposition</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Start"
              type="datetime-local"
              value={breakForm.startTime}
              onChange={(event) => handleBreakFormChange('startTime', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Ende"
              type="datetime-local"
              value={breakForm.endTime}
              onChange={(event) => handleBreakFormChange('endTime', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Grund der Änderung"
              value={breakForm.correctionReason}
              onChange={(event) => handleBreakFormChange('correctionReason', event.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBreakDialog} disabled={breakSaving}>Abbrechen</Button>
          <Button onClick={handleSaveBreak} variant="contained" disabled={breakSaving}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  const buildCsv = useCallback((header, rows) => {
    const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    return `\uFEFF${header.map(escape).join(';')}\n${rows.map(row => row.map(escape).join(';')).join('\n')}`;
  }, []);

  const downloadCsv = useCallback((filename, csv) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleExportCsv = useCallback(() => {
    if (currentTab === TAB_WORK_TIMES) {
      const dateSuffix = `${reportBounds.start.toISOString().slice(0, 10)}_bis_${reportBounds.end.toISOString().slice(0, 10)}`;
      const header = ['Datum', 'Fahrer-ID', 'Fahrer', 'Schicht', 'Start', 'Ende', 'Status', 'Online-Brutto-Minuten', 'Pausen-Minuten', 'Nicht-abgezogene-Minuten', 'Netto-Minuten', 'Soll-Minuten', 'Überstunden-Minuten', 'Pausentypen'];
      const rows = filteredWorkTimeRows.map(row => [
        row.dateKey,
        row.driverId,
        row.displayDriverName,
        row.shiftNumber,
        formatDateTime(row.shiftStart),
        row.isActive ? 'laufend' : formatDateTime(row.displayShiftEnd || row.shiftEnd),
        row.isActive ? 'Aktiv' : 'Abgeschlossen',
        row.grossMinutes,
        row.breakMinutes,
        row.nonDeductibleBreakMinutes || 0,
        row.netMinutes,
        row.targetMinutes,
        row.overtimeMinutes,
        (row.breakTypes || []).join(', ')
      ]);
      downloadCsv(`arbeitszeiten-${dateSuffix}.csv`, buildCsv(header, rows));
      return;
    }

    const dateSuffix = `${reportBounds.start.toISOString().slice(0, 10)}_bis_${reportBounds.end.toISOString().slice(0, 10)}`;
    if (currentTab === TAB_REVENUE) {
      // Capture-ID ist der Abgleichschlüssel zu den PayPal-Reports (8.4).
      const header = ['Fahrt-ID', 'Datum', 'Fahrer', 'Start', 'Ziel', 'Fahrpreis', 'Rabatt', 'Kassiert', 'Zahlungsart', 'Zahlungsstatus', 'Capture-Betrag', 'Erstattet', 'PayPal-Capture-ID', 'Umstellungsgrund'];
      const rows = revenueReportRows.map(ride => [
        ride.id,
        formatDateTime(ride.refDate),
        ride.driverName || '',
        ride.start || '',
        ride.destination || '',
        ride.billableFare,
        ride.discountEur > 0 ? ride.discountEur.toFixed(2) : '',
        ride.collectedFare.toFixed(2),
        getPaymentCategoryLabel(ride.paymentCategory),
        ride.paymentStatus || '',
        ride.paymentCapturedAmountCents ? (Number(ride.paymentCapturedAmountCents) / 100).toFixed(2) : '',
        ride.refundedEur > 0 ? ride.refundedEur.toFixed(2) : '',
        ride.paymentCaptureId || ride.paypalCaptureId || '',
        ride.paymentFallbackReason || ''
      ]);
      downloadCsv(`umsatzanalyse-${dateSuffix}.csv`, buildCsv(header, rows));
      return;
    }

    if (currentTab === TAB_RECEIPTS) {
      const header = ['Nummer', 'Ausgestellt', 'Kunde', 'Von', 'Nach', 'km', 'Fahrpreis', 'Rabatt', 'Kassiert', 'USt-Satz', 'USt', 'Zahlungsart', 'Versandstatus', 'Empfaenger', 'Fahrt-ID'];
      const rows = filteredReceiptRows.map(r => [
        r.receiptNumber || '',
        formatDateTime(r.issuedAt),
        r.customerName || (r.isPhoneCustomer ? 'Telefonkunde' : ''),
        r.ride?.start || '',
        r.ride?.destination || '',
        r.ride?.distanceKm ?? '',
        r.amounts?.grossEur ?? '',
        r.amounts?.discountEur ? r.amounts.discountEur : '',
        r.amounts?.payableEur ?? '',
        r.amounts?.vatRate != null ? `${r.amounts.vatRate}%` : '',
        r.amounts?.vatEur ?? '',
        r.ride?.paymentMethod === 'paypal' ? 'PayPal (App)' : 'Im Fahrzeug',
        receiptEmailStatusMeta(r.email?.status).label,
        r.email?.to || '',
        r.rideId || r.id
      ]);
      downloadCsv(`quittungen-${dateSuffix}.csv`, buildCsv(header, rows));
      return;
    }

    if (currentTab === TAB_OPERATORS) {
      const header = ['Fahrt-ID', 'Datum', 'Auftraggeber-Unternehmer', 'Ausführender Unternehmer', 'Fahrer', 'Start', 'Ziel', 'Tatsächlicher Preis', 'Geschätzter Preis', 'Abrechnungsbetrag', 'Preisquelle', 'Zahlungsart', 'Vermittlungsart'];
      const rows = operatorReportRows.map(ride => [
        ride.id,
        formatDateTime(ride.refDate),
        ride.createdOperatorName,
        ride.driverOperatorName,
        ride.driverName || '',
        ride.start || '',
        ride.destination || '',
        ride.fare ?? '',
        ride.estimatedEarningEur ?? '',
        ride.billableFare,
        ride.fareSource,
        getPaymentCategoryLabel(ride.paymentCategory),
        getMediationLabel(ride.mediationType)
      ]);
      downloadCsv(`unternehmer-vermittlungen-${dateSuffix}.csv`, buildCsv(header, rows));
      return;
    }

    const header = ['Fahrt-ID', 'Datum', 'Fahrer', 'Typ', 'Start', 'Ziel', 'Fahrzeug', 'Tatsächlicher Preis', 'Geschätzter Preis', 'Abrechnungsbetrag', 'Preisquelle', 'Zahlungsart'];
    const rows = driverReportRows.map(ride => [
      ride.id,
      formatDateTime(ride.refDate),
      ride.driverName || '',
      ride.reportType,
      ride.start || '',
      ride.destination || '',
      [ride.driverModel, ride.driverLicensePlate].filter(Boolean).join(' / '),
      ride.fare ?? '',
      ride.estimatedEarningEur ?? '',
      ride.billableFare,
      ride.fareSource,
      getPaymentCategoryLabel(getPaymentCategory(ride))
    ]);
    downloadCsv(`fahrerleistung-${dateSuffix}.csv`, buildCsv(header, rows));
  }, [currentTab, reportBounds, filteredWorkTimeRows, operatorReportRows, driverReportRows, revenueReportRows, filteredReceiptRows, receiptEmailStatusMeta, formatDateTime, buildCsv, downloadCsv]);

  const handleExportPdf = useCallback(() => {
    const dateSuffix = `${reportBounds.start.toISOString().slice(0, 10)} bis ${reportBounds.end.toISOString().slice(0, 10)}`;
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const isOperatorReport = currentTab === TAB_OPERATORS;
    const rows = isOperatorReport ? operatorReportRows : driverReportRows;
    const total = rows.reduce((sum, ride) => sum + ride.billableFare, 0);
    let y = 14;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text(isOperatorReport ? 'Unternehmer-Vermittlungsnachweis' : 'Fahrerleistung', 14, y);
    y += 8;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text(`Zeitraum: ${dateSuffix}`, 14, y);
    y += 6;
    pdf.text(`Fahrten: ${rows.length}   Summe: ${total.toFixed(2)} EUR`, 14, y);
    y += 10;

    const headers = isOperatorReport
      ? ['Datum', 'Auftraggeber', 'Ausführer', 'Fahrer', 'Betrag', 'Art']
      : ['Datum', 'Fahrer', 'Typ', 'Start', 'Ziel', 'Betrag'];
    const widths = isOperatorReport ? [34, 50, 50, 42, 24, 28] : [34, 42, 34, 58, 58, 24];

    pdf.setFont('helvetica', 'bold');
    let x = 14;
    headers.forEach((header, index) => {
      pdf.text(header, x, y);
      x += widths[index];
    });
    y += 5;
    pdf.setFont('helvetica', 'normal');

    rows.forEach((ride) => {
      if (y > 190) {
        pdf.addPage();
        y = 14;
      }
      const values = isOperatorReport
          ? [
            formatDateTime(ride.refDate),
            ride.createdOperatorName,
            ride.driverOperatorName,
            ride.driverName || '-',
            `${ride.billableFare.toFixed(2)} EUR`,
            getMediationLabel(ride.mediationType)
          ]
        : [
            formatDateTime(ride.refDate),
            ride.driverName || '-',
            ride.reportType,
            ride.start || '-',
            ride.destination || '-',
            `${ride.billableFare.toFixed(2)} EUR`
          ];
      x = 14;
      values.forEach((value, index) => {
        const text = pdf.splitTextToSize(String(value), widths[index] - 2)[0] || '';
        pdf.text(text, x, y);
        x += widths[index];
      });
      y += 6;
    });

    pdf.save(`${isOperatorReport ? 'unternehmer-nachweis' : 'fahrerleistung'}-${new Date().toISOString().slice(0, 10)}.pdf`);
  }, [currentTab, reportBounds, operatorReportRows, driverReportRows, formatDateTime]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Übersichtsdaten – nur neu berechnet wenn rides oder dateRange sich ändern.
  // Enthält O(n²)-Logik (filter in map), daher ist Memoization hier besonders wichtig.
  const overviewChartData = useMemo(() => {
    const totalRides = overviewRides.length;
    const ridesByStatus = overviewRides.reduce((acc, ride) => {
      acc[ride.status] = (acc[ride.status] || 0) + 1;
      return acc;
    }, {});

    const today = new Date();
    const daysToShow = dateRange === FILTER_WEEK ? 7 : 30;

    // Timestamps vorberechnen um wiederholte Date-Konstruktionen im inneren Loop zu vermeiden
    const dayBuckets = Array.from({ length: daysToShow }, (_, index) => {
      const start = new Date(today);
      start.setDate(start.getDate() - (daysToShow - 1 - index));
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      return { date: start, startMs: start.getTime(), endMs: end.getTime() };
    });

    const completedOverviewRides = overviewRides.filter(r => r.status === 'completed' || r.status === 'archived');
    const averageFare = completedOverviewRides.length > 0
      ? completedOverviewRides.reduce((sum, r) => sum + (r.estimatedEarningEur || r.fare || 0), 0) / completedOverviewRides.length
      : 0;

    const ridesByDay = dayBuckets.map(({ date, startMs, endMs }) => ({
      date,
      count: overviewRides.filter(r => {
        if (!r.timestamp) return false;
        const ms = new Date(r.timestamp).getTime();
        return ms >= startMs && ms < endMs;
      }).length,
    }));

    const ratingsByDay = dayBuckets.map(({ date, startMs, endMs }) => {
      const dayRides = completedOverviewRides.filter(r => {
        if (!r.completionTimestamp) return false;
        const ms = new Date(r.completionTimestamp).getTime();
        return ms >= startMs && ms < endMs;
      });
      return {
        date,
        rating: dayRides.length > 0
          ? dayRides.reduce((sum, r) => sum + (r.rating || 0), 0) / dayRides.length
          : 0,
      };
    });

    return { totalRides, ridesByStatus, ridesByDay, averageFare, ratingsByDay };
  }, [overviewRides, dateRange]);
  
  // Übersichts-Tab-Inhalt – nur neu gerendert wenn overviewChartData oder drivers sich ändern
  const renderOverviewTab = useMemo(() => {
    const {
      totalRides,
      ridesByStatus,
      ridesByDay,
      averageFare,
      ratingsByDay
    } = overviewChartData;
    
    // Daten für das Fahrtenstatus-Tortendiagramm
    const pieData = {
      labels: Object.keys(ridesByStatus).map(status => {
        switch (status) {
          case 'open': return 'Angefragt';
          case 'accepted': return 'Akzeptiert';
          case 'pre_accepted': return 'Vom Fahrer angenommen';
          case 'in_progress': return 'Zielfahrt';
          case 'arrived': return 'Angekommen';
          case 'completed': return 'Abgeschlossen';
          case 'archived': return 'Archiviert';
          case 'cancelled': return 'Storniert';
          case 'cancelled_by_driver': return 'Vom Fahrer storniert';
          case 'scheduled': return 'Geplant';
          default: return status;
        }
      }),
      datasets: [
        {
          data: Object.values(ridesByStatus),
          backgroundColor: [
            '#FFCE56', // Gelb für angefragt/offen
            '#36A2EB', // Blau für akzeptiert
            '#32a4a8', // Hellblau für vom Fahrer angenommen
            '#4BC0C0', // Türkis für Zielfahrt
            '#8bc34a', // Hellgrün für angekommen
            '#4CAF50', // Grün für abgeschlossen
            '#2E7D32', // Dunkelgrün für archiviert
            '#FF6384', // Rot für storniert
            '#d32f2f', // Dunkelrot für vom Fahrer storniert
            '#9C27B0'  // Lila für geplant
          ],
          borderWidth: 1
        }
      ]
    };
    
    // Optionen für das Tortendiagramm
    const pieOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
        },
        title: {
          display: true,
          text: 'Fahrten nach Status'
        }
      }
    };
    
    // Daten für das Balkendiagramm der Fahrten pro Tag
    const barData = {
      labels: ridesByDay.map(data => data.date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric' })),
      datasets: [
        {
          label: 'Anzahl Fahrten',
          data: ridesByDay.map(data => data.count),
          backgroundColor: '#FFD700', // Gold/Gelb für Taxis
          borderColor: '#212121', // Dunkelgrau
          borderWidth: 1
        }
      ]
    };
    
    // Optionen für das Balkendiagramm
    const barOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Fahrten pro Tag'
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0
          }
        }
      }
    };
    
    // Daten für das Lininiendiagramm der Bewertungen
    const lineData = {
      labels: ratingsByDay.map(data => data.date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric' })),
      datasets: [
        {
          label: 'Durchschnittliche Bewertung',
          data: ratingsByDay.map(data => data.rating),
          backgroundColor: 'rgba(33, 33, 33, 0.2)',
          borderColor: '#212121',
          borderWidth: 2,
          tension: 0.3,
          fill: true
        }
      ]
    };
    
    // Optionen für das Liniendiagramm
    const lineOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Bewertungstrend'
        }
      },
      scales: {
        y: {
          beginAtZero: false,
          min: 0,
          max: 5,
          ticks: {
            stepSize: 1
          }
        }
      }
    };
    
    return (
      <Grid container spacing={3}>
        {/* KPI-Karten */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              backgroundImage: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
              color: '#212121'
            }}
          >
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {totalRides}
            </Typography>
            <Typography variant="body1">
              Fahrten gesamt
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              backgroundImage: 'linear-gradient(135deg, #212121 0%, #424242 100%)',
              color: '#FFC107'
            }}
          >
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {ridesByStatus['completed'] || 0}
            </Typography>
            <Typography variant="body1">
              Abgeschlossene Fahrten
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              backgroundImage: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
              color: '#212121'
            }}
          >
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {averageFare.toFixed(2)} €
            </Typography>
            <Typography variant="body1">
              Durchschnittlicher Fahrpreis
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              backgroundImage: 'linear-gradient(135deg, #212121 0%, #424242 100%)',
              color: '#FFC107'
            }}
          >
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {drivers ? drivers.filter(isDriverReportRecord).length : 0}
            </Typography>
            <Typography variant="body1">
              Aktive Fahrer
            </Typography>
          </Paper>
        </Grid>
        
        {/* Diagramme */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Pie data={pieData} options={pieOptions} />
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Bar data={barData} options={barOptions} />
          </Paper>
        </Grid>
        
        <Grid item xs={12}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Line data={lineData} options={lineOptions} />
          </Paper>
        </Grid>
      </Grid>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewChartData, drivers]);

  // Fahrten-Tab-Inhalt
  const renderRidesTab = () => {
    // TODO: Detaillierte Fahrtenanalyse implementieren
    return (
      <Typography variant="body1">
        Fahrtenanalyse wird implementiert...
      </Typography>
    );
  };
  
  // Fahrer-Tab-Inhalt
  const renderDriversTab = () => {
    const total = driverReportRows.reduce((sum, ride) => sum + ride.billableFare, 0);
    const actualCount = driverReportRows.filter(ride => ride.fareSource === 'Tatsächlich').length;
    const walkInCount = driverReportRows.filter(ride => ride.reportType === 'Einsteiger-Tour').length;

    return (
      <Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Fahrer</InputLabel>
            <Select
              label="Fahrer"
              value={selectedDriverId}
              onChange={(event) => setSelectedDriverId(event.target.value)}
            >
              <MenuItem value="all">Alle Fahrer</MenuItem>
              {drivers.filter(isDriverReportRecord).map(driver => (
                <MenuItem key={driver.id} value={driver.id}>
                  {getDriverDisplayName(driver)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
            CSV exportieren
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handleExportPdf}>
            PDF exportieren
          </Button>
        </Stack>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Fahrten</Typography>
              <Typography variant="h5">{driverReportRows.length}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Umsatz</Typography>
              <Typography variant="h5">{formatMoney(total)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Tatsächliche Preise</Typography>
              <Typography variant="h5">{actualCount}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Einsteiger-Touren</Typography>
              <Typography variant="h5">{walkInCount}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Fahrer</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>Ziel</TableCell>
                <TableCell align="right">Betrag</TableCell>
                <TableCell>Preisquelle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {driverReportRows.map(ride => (
                <TableRow key={ride.id}>
                  <TableCell>{formatDateTime(ride.refDate)}</TableCell>
                  <TableCell>{ride.driverName || '-'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={ride.reportType} color={ride.reportType === 'Einsteiger-Tour' ? 'warning' : 'default'} />
                  </TableCell>
                  <TableCell>{ride.start || '-'}</TableCell>
                  <TableCell>{ride.destination || 'Kein Ziel angegeben'}</TableCell>
                  <TableCell align="right">{formatMoney(ride.billableFare)}</TableCell>
                  <TableCell>{ride.fareSource}</TableCell>
                </TableRow>
              ))}
              {driverReportRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">Keine abgeschlossenen Fahrten im Zeitraum</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>
    );
  };

  const renderWorkTimesTab = () => {
    return (
      <Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Fahrer</InputLabel>
            <Select
              label="Fahrer"
              value={selectedWorkTimeDriverId}
              onChange={(event) => setSelectedWorkTimeDriverId(event.target.value)}
            >
              <MenuItem value="all">Alle Fahrer</MenuItem>
              {workTimeDriverOptions.map(driver => (
                <MenuItem key={driver.id} value={driver.id}>
                  {getDriverDisplayName(driver)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={selectedWorkTimeStatus}
              onChange={(event) => setSelectedWorkTimeStatus(event.target.value)}
            >
              <MenuItem value="all">Alle Schichten</MenuItem>
              <MenuItem value="active">Nur laufende</MenuItem>
              <MenuItem value="completed">Nur abgeschlossene</MenuItem>
              <MenuItem value="overtime">Nur Überstunden</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Pausentyp</InputLabel>
            <Select
              label="Pausentyp"
              value={selectedWorkTimeBreakType}
              onChange={(event) => setSelectedWorkTimeBreakType(event.target.value)}
            >
              <MenuItem value="all">Alle Typen</MenuItem>
              {workTimeBreakTypes.map(type => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
            CSV exportieren
          </Button>
        </Stack>

        {workTimeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {workTimeError}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Schichten</Typography>
              <Typography variant="h5">{workTimeSummary.shifts}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Online-Brutto</Typography>
              <Typography variant="h5">{formatMinutes(workTimeSummary.grossMinutes)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Pausen abgezogen</Typography>
              <Typography variant="h5">{formatMinutes(workTimeSummary.breakMinutes)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Nicht abgezogen</Typography>
              <Typography variant="h5">{formatMinutes(workTimeSummary.nonDeductibleBreakMinutes || 0)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Netto</Typography>
              <Typography variant="h5">{formatMinutes(workTimeSummary.netMinutes)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Überstunden</Typography>
              <Typography variant="h5">{formatMinutes(workTimeSummary.overtimeMinutes)}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Fahrer</TableCell>
                <TableCell>Schicht</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>Ende</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Online-Brutto</TableCell>
                <TableCell align="right">Pausen abgezogen</TableCell>
                <TableCell align="right">Nicht abgezogen</TableCell>
                <TableCell align="right">Netto</TableCell>
                <TableCell align="right">Überstunden</TableCell>
                <TableCell>Pausentypen</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workTimeLoading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">Arbeitszeiten werden geladen...</TableCell>
                </TableRow>
              ) : filteredWorkTimeRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">Keine Arbeitszeiten im Zeitraum</TableCell>
                </TableRow>
              ) : (
                filteredWorkTimeRows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>{row.dateKey}</TableCell>
                    <TableCell>{row.displayDriverName}</TableCell>
                    <TableCell>#{row.shiftNumber}</TableCell>
                    <TableCell>{formatDateTime(row.shiftStart)}</TableCell>
                    <TableCell>{row.isActive ? 'läuft' : formatDateTime(row.displayShiftEnd || row.shiftEnd)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          size="small"
                          color={row.isActive ? 'success' : 'default'}
                          label={row.isActive ? 'Aktiv' : 'Abgeschlossen'}
                        />
                        {row.hasOpenItems && !row.isActive && (
                          <Chip size="small" color="warning" label="Offen" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{formatMinutes(row.grossMinutes)}</TableCell>
                    <TableCell align="right">{formatMinutes(row.breakMinutes)}</TableCell>
                    <TableCell align="right">{formatMinutes(row.nonDeductibleBreakMinutes || 0)}</TableCell>
                    <TableCell align="right">{formatMinutes(row.netMinutes)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        component="span"
                        color={row.overtimeMinutes > 0 ? 'warning.main' : 'text.primary'}
                        fontWeight={row.overtimeMinutes > 0 ? 700 : 400}
                      >
                        {formatMinutes(row.overtimeMinutes)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(row.breakTypes || []).length > 0 ? (
                          row.breakTypes.map(type => (
                            <Chip key={type} size="small" label={type} variant="outlined" />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        title="Schichtdetails anzeigen"
                        onClick={() => handleOpenWorkTimeDetails(row)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {renderWorkTimeDialogs()}
      </Box>
    );
  };
  
  // Umsatz-Tab-Inhalt: Aufschlüsselung nach Zahlungsart (Konzept 8.5).
  // Gebühren/Nettoauszahlungen bewusst nicht hier — dafür ist das
  // PayPal-Geschäftskonto des Unternehmers zuständig (Konzept 8.4).
  const renderRevenueTab = () => {
    const summary = revenueSummary;
    const appShare = summary.total > 0 ? (summary.appPaid / summary.total) * 100 : 0;

    // Tages-Buckets über den gewählten Zeitraum (max. 62 Tage tagesgenau,
    // darüber monatsweise, damit das Diagramm lesbar bleibt)
    const dayMs = 24 * 60 * 60 * 1000;
    const rangeDays = Math.max(1, Math.ceil((reportBounds.end - reportBounds.start) / dayMs));
    const useMonthly = rangeDays > 62;
    const bucketKey = (date) => {
      if (!date) return null;
      return useMonthly
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };
    const buckets = new Map();
    const cursor = new Date(reportBounds.start);
    while (cursor <= reportBounds.end) {
      buckets.set(bucketKey(cursor), { appPaid: 0, inVehicle: 0 });
      if (useMonthly) {
        cursor.setMonth(cursor.getMonth() + 1);
      } else {
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    for (const ride of revenueReportRows) {
      const key = bucketKey(ride.refDate);
      const bucket = key ? buckets.get(key) : null;
      if (!bucket) continue;
      if (ride.paymentCategory === 'app_paid') {
        bucket.appPaid += ride.collectedFare;
      } else {
        bucket.inVehicle += ride.collectedFare;
      }
    }
    const bucketLabels = Array.from(buckets.keys());
    const stackedBarData = {
      labels: bucketLabels.map(key => useMonthly
        ? dayjs(`${key}-01`).format('MMM YYYY')
        : dayjs(key).format('DD.MM.')),
      datasets: [
        {
          label: 'Per App bezahlt',
          data: bucketLabels.map(key => Number(buckets.get(key).appPaid.toFixed(2))),
          backgroundColor: '#1976D2'
        },
        {
          label: 'Im Fahrzeug',
          data: bucketLabels.map(key => Number(buckets.get(key).inVehicle.toFixed(2))),
          backgroundColor: '#FFD700'
        }
      ]
    };
    const stackedBarOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' },
        title: { display: true, text: `Umsatz pro ${useMonthly ? 'Monat' : 'Tag'} nach Zahlungsart` }
      },
      scales: {
        x: { stacked: true },
        y: { stacked: true, beginAtZero: true }
      }
    };

    const categoryCounts = revenueReportRows.reduce((acc, ride) => {
      acc[ride.paymentCategory] = (acc[ride.paymentCategory] || 0) + 1;
      return acc;
    }, {});
    const pieCategories = Object.keys(categoryCounts);
    const pieColors = {
      app_paid: '#1976D2',
      in_vehicle: '#FFD700',
      app_failed: '#d32f2f',
      app_to_cash: '#ED6C02',
      app_pending: '#9C27B0'
    };
    const paymentPieData = {
      labels: pieCategories.map(getPaymentCategoryLabel),
      datasets: [{
        data: pieCategories.map(category => categoryCounts[category]),
        backgroundColor: pieCategories.map(category => pieColors[category] || '#9E9E9E'),
        borderWidth: 1
      }]
    };
    const paymentPieOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'right' },
        title: { display: true, text: 'Fahrten nach Zahlungsart' }
      }
    };

    const kpis = [
      { label: 'Gesamtumsatz', value: formatMoney(summary.total), hint: summary.discount > 0 ? 'kassiert' : undefined },
      ...(summary.discount > 0 ? [
        { label: 'Treuerabatte', value: `− ${formatMoney(summary.discount)}`, hint: `${summary.discountCount} ${summary.discountCount === 1 ? 'Fahrt' : 'Fahrten'}` },
        { label: 'Fahrpreis brutto', value: formatMoney(summary.gross), hint: 'vor Rabatt' }
      ] : []),
      { label: 'Umsatz per App', value: formatMoney(summary.appPaid), hint: `${summary.appPaidCount} Fahrten` },
      { label: 'Umsatz im Fahrzeug', value: formatMoney(summary.inVehicle) },
      { label: 'App-Anteil', value: `${appShare.toFixed(1)} %` },
      { label: 'App-Zahlung fehlgeschlagen', value: String(summary.failedCount), hint: summary.toCashCount > 0 ? `+ ${summary.toCashCount}× bar kassiert` : undefined },
      { label: 'Erstattet (PayPal)', value: formatMoney(summary.refunded) }
    ];

    return (
      <Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Fahrer</InputLabel>
            <Select
              label="Fahrer"
              value={selectedDriverId}
              onChange={(event) => setSelectedDriverId(event.target.value)}
            >
              <MenuItem value="all">Alle Fahrer</MenuItem>
              {drivers.filter(isDriverReportRecord).map(driver => (
                <MenuItem key={driver.id} value={driver.id}>
                  {getDriverDisplayName(driver)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
            CSV exportieren
          </Button>
        </Stack>

        <Alert severity="info" sx={{ mb: 3 }}>
          Umsatz = tatsächlich kassierte Beträge (Fahrpreis abzüglich
          Treuerabatt). Ohne PayPal-Gebühren — Gebühren, Auszahlungen und
          Kontoauszüge finden Sie im PayPal-Geschäftskonto, der Abgleich
          erfolgt über die Capture-ID im CSV-Export.
        </Alert>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {kpis.map(kpi => (
            <Grid item xs={6} md={2} key={kpi.label}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                <Typography variant="h6">{kpi.value}</Typography>
                {kpi.hint && (
                  <Typography variant="caption" color="text.secondary">{kpi.hint}</Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, height: 320 }}>
              <Bar data={stackedBarData} options={stackedBarOptions} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: 320 }}>
              <Pie data={paymentPieData} options={paymentPieOptions} />
            </Paper>
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Fahrer</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>Ziel</TableCell>
                <TableCell align="right">Fahrpreis</TableCell>
                <TableCell align="right">Rabatt</TableCell>
                <TableCell align="right">Kassiert</TableCell>
                <TableCell>Zahlungsart</TableCell>
                <TableCell>Zahlungsstatus</TableCell>
                <TableCell align="right">Erstattet</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {revenueReportRows.map(ride => (
                <TableRow key={ride.id}>
                  <TableCell>{formatDateTime(ride.refDate)}</TableCell>
                  <TableCell>{ride.driverName || '-'}</TableCell>
                  <TableCell>{ride.start || '-'}</TableCell>
                  <TableCell>{ride.destination || '-'}</TableCell>
                  <TableCell align="right">{formatMoney(ride.billableFare)}</TableCell>
                  <TableCell align="right">
                    {ride.discountEur > 0 ? `− ${formatMoney(ride.discountEur)}` : '–'}
                  </TableCell>
                  <TableCell align="right">{formatMoney(ride.collectedFare)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={getPaymentCategoryLabel(ride.paymentCategory)}
                      color={
                        ride.paymentCategory === 'app_paid' ? 'primary' :
                        ride.paymentCategory === 'app_failed' ? 'error' :
                        ride.paymentCategory === 'app_to_cash' ? 'warning' :
                        ride.paymentCategory === 'app_pending' ? 'secondary' : 'default'
                      }
                      variant={ride.paymentCategory === 'in_vehicle' ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell>{ride.paymentStatus || '-'}</TableCell>
                  <TableCell align="right">
                    {ride.refundedEur > 0 ? formatMoney(ride.refundedEur) : '-'}
                  </TableCell>
                </TableRow>
              ))}
              {revenueReportRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">Keine abgeschlossenen Fahrten im Zeitraum</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const handleExportPayoutCsv = () => {
    const dateSuffix = `${reportBounds.start.toISOString().slice(0, 10)}_bis_${reportBounds.end.toISOString().slice(0, 10)}`;
    const header = [
      'Unternehmer',
      'App-Fahrten',
      'Brutto am Hauptkonto eingegangen',
      'Davon erstattet',
      'Auszuzahlen (brutto)',
      'Auszahlungsart',
      'Auszahlungsziel',
      'Im Fahrzeug kassiert (info)'
    ];
    const payoutMethodLabel = (method) => method === 'paypal' ? 'PayPal' : method === 'bank' ? 'Banküberweisung' : 'nicht hinterlegt';
    const rows = operatorPayoutRows.map(entry => [
      entry.operatorName,
      entry.appPaidCount,
      entry.appPaidGross.toFixed(2),
      entry.appPaidRefunded.toFixed(2),
      entry.payoutDue.toFixed(2),
      payoutMethodLabel(entry.payoutMethod),
      entry.payoutTarget,
      entry.inVehicleGross.toFixed(2)
    ]);
    downloadCsv(`auszahlungen-sub-unternehmer-${dateSuffix}.csv`, buildCsv(header, rows));
  };


  const renderReceiptsTab = () => {
    const s = receiptSummary;
    const kpis = [
      { label: 'Quittungen', value: String(s.total), hint: `Summe ${formatMoney(s.sum)}` },
      { label: 'Versendet', value: String(s.sent) },
      { label: 'Fehlgeschlagen', value: String(s.failed) },
      { label: 'Ohne Versand', value: String(s.noEmail + s.pending) },
    ];
    return (
      <Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Versandstatus</InputLabel>
            <Select
              label="Versandstatus"
              value={receiptStatusFilter}
              onChange={(e) => setReceiptStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Alle</MenuItem>
              <MenuItem value="sent">Versendet</MenuItem>
              <MenuItem value="failed">Fehlgeschlagen</MenuItem>
              <MenuItem value="queued">In Zustellung</MenuItem>
              <MenuItem value="no_address">Keine Adresse</MenuItem>
              <MenuItem value="not_configured">Nur In-App</MenuItem>
              <MenuItem value="opted_out">Abgemeldet</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
            CSV exportieren
          </Button>
          <Button variant="outlined" onClick={() => reloadReceipts()}>
            Aktualisieren
          </Button>
        </Stack>

        {receiptsError && <Alert severity="error" sx={{ mb: 2 }}>{receiptsError}</Alert>}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {kpis.map(kpi => (
            <Grid item xs={6} md={3} key={kpi.label}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                <Typography variant="h6">{kpi.value}</Typography>
                {kpi.hint && <Typography variant="caption" color="text.secondary">{kpi.hint}</Typography>}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Alert severity="info" sx={{ mb: 3 }}>
          Quittungen bleiben als Buchführungsunterlagen dauerhaft erhalten (auch nach
          Löschung der Fahrt). „Erneut senden" verschickt die bereits erzeugte Quittung –
          etwa nach fehlgeschlagenem Versand oder an eine korrigierte Adresse.
        </Alert>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Nr.</TableCell>
                <TableCell>Kunde</TableCell>
                <TableCell>Strecke</TableCell>
                <TableCell align="right">Betrag</TableCell>
                <TableCell>Zahlung</TableCell>
                <TableCell>Versand</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReceiptRows.map(receipt => {
                const meta = receiptEmailStatusMeta(receipt.email?.status);
                return (
                  <TableRow key={receipt.id}>
                    <TableCell>{formatDateTime(receipt.issuedAt)}</TableCell>
                    <TableCell>{receipt.receiptNumber || '—'}</TableCell>
                    <TableCell>{receipt.customerName || (receipt.isPhoneCustomer ? 'Telefonkunde' : '—')}</TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        title={`${receipt.ride?.start || ''} → ${receipt.ride?.destination || ''}`}
                      >
                        {receipt.ride?.start || '—'} → {receipt.ride?.destination || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatMoney(receipt.amounts?.payableEur)}</TableCell>
                    <TableCell>{receipt.ride?.paymentMethod === 'paypal' ? 'PayPal (App)' : 'Im Fahrzeug'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={meta.label}
                        color={meta.color}
                        variant={meta.color === 'default' ? 'outlined' : 'filled'}
                      />
                      {receipt.email?.to && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {receipt.email.to}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenReceiptPdf(receipt)}
                          disabled={receiptOpeningId === receipt.id}
                        >
                          {receiptOpeningId === receipt.id ? '…' : 'PDF'}
                        </Button>
                        <Button size="small" variant="text" onClick={() => handleOpenResend(receipt)}>
                          Erneut senden
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!receiptsLoading && filteredReceiptRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {receiptRows.length === 0
                      ? 'Keine Quittungen im Zeitraum'
                      : 'Keine Quittungen für diesen Filter'}
                  </TableCell>
                </TableRow>
              )}
              {receiptsLoading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">Quittungen werden geladen…</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={Boolean(resendReceipt)}
          onClose={() => !resendBusy && setResendReceipt(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Quittung erneut senden</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Die bereits erstellte Quittung{resendReceipt?.receiptNumber ? ` Nr. ${resendReceipt.receiptNumber}` : ''} wird
              unverändert erneut als PDF an die angegebene Adresse gesendet.
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              type="email"
              label="E-Mail-Adresse"
              placeholder="name@beispiel.de"
              value={resendEmail}
              onChange={(e) => { setResendEmail(e.target.value); setResendError(''); }}
              error={!!resendError}
              helperText={resendError}
              disabled={resendBusy}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResendReceipt(null)} disabled={resendBusy}>Abbrechen</Button>
            <Button
              onClick={handleSubmitResend}
              variant="contained"
              disabled={resendBusy || !resendEmail.trim()}
            >
              {resendBusy ? 'Wird gesendet…' : 'Erneut senden'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const renderOperatorsTab = () => {
    const total = operatorReportRows.reduce((sum, ride) => sum + ride.billableFare, 0);
    const crossOperatorCount = operatorReportRows.filter(ride => ride.mediationType === 'cross_operator').length;
    const totalPayoutDue = operatorPayoutRows.reduce((sum, entry) => sum + entry.payoutDue, 0);
    const baseOperatorOptions = operators.length > 0
      ? operators
      : Array.from(new Map(completedReportRides.flatMap(ride => [
          [ride.createdByOperatorId || ride.preferredOperatorId, ride.createdByOperatorName || ride.preferredOperatorName],
          [ride.driverOperatorId || ride.vehicleOperatorId, ride.driverOperatorName]
        ]).filter(([id]) => id).map(([id, name]) => [id, { id, name: name || id }])).values());
    const hasCentralCreatedRides = completedReportRides.some(ride => getCreatedByCentralName(ride));
    const hasCentralExecutedRides = completedReportRides.some(ride => (
      !ride.driverOperatorId &&
      !ride.vehicleOperatorId &&
      (ride.driverId || ride.driverVehicleId || ride.driverName || ride.driverLicensePlate)
    ));
    const operatorOptions = hasCentralCreatedRides || hasCentralExecutedRides
      ? [{ id: CENTRAL_CREATED_OPERATOR_ID, name: 'Hauptzentrale' }, ...baseOperatorOptions]
      : baseOperatorOptions;

    return (
      <Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Auftraggeber</InputLabel>
            <Select
              label="Auftraggeber"
              value={selectedCreatedOperatorId}
              onChange={(event) => setSelectedCreatedOperatorId(event.target.value)}
            >
              <MenuItem value="all">Alle</MenuItem>
              {operatorOptions.map(operator => (
                <MenuItem key={operator.id} value={operator.id}>{operator.name || operator.id}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Ausführer</InputLabel>
            <Select
              label="Ausführer"
              value={selectedDriverOperatorId}
              onChange={(event) => setSelectedDriverOperatorId(event.target.value)}
            >
              <MenuItem value="all">Alle</MenuItem>
              {operatorOptions.map(operator => (
                <MenuItem key={operator.id} value={operator.id}>{operator.name || operator.id}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Vermittlung</InputLabel>
            <Select
              label="Vermittlung"
              value={selectedMediationStatus}
              onChange={(event) => setSelectedMediationStatus(event.target.value)}
            >
              <MenuItem value="all">Alle</MenuItem>
              <MenuItem value="cross_operator">An fremden Unternehmer vermittelt</MenuItem>
              <MenuItem value="same_operator">Eigen gefahren</MenuItem>
              <MenuItem value="central">Hauptzentrale</MenuItem>
              <MenuItem value="unknown">Unklar</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
            CSV exportieren
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handleExportPdf}>
            PDF exportieren
          </Button>
        </Stack>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Fahrten</Typography>
              <Typography variant="h5">{operatorReportRows.length}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Vermittelte Fahrten</Typography>
              <Typography variant="h5">{crossOperatorCount}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Umsatz</Typography>
              <Typography variant="h5">{formatMoney(total)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">An Sub-Unternehmer auszuzahlen</Typography>
              <Typography variant="h5" color={totalPayoutDue > 0 ? 'warning.main' : 'text.primary'}>
                {formatMoney(totalPayoutDue)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Auszahlungsübersicht (Konzept 8.7): App-Zahlungen am Hauptkonto,
            die ein Sub-Unternehmer erfahren hat → interne Auszahlung */}
        {operatorPayoutRows.length > 0 && (
          <Paper sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, pt: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Auszahlungsübersicht App-Zahlungen
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Bruttobeträge im gewählten Zeitraum, ohne PayPal-Gebühren und ohne Vermittlungsprovision.
                  Fahrzeug-Umsatz hat der Unternehmer bereits selbst kassiert.
                </Typography>
              </Box>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportPayoutCsv}>
                Auszahlungs-CSV
              </Button>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ausführender Unternehmer</TableCell>
                    <TableCell align="right">App-Fahrten</TableCell>
                    <TableCell align="right">Brutto am Hauptkonto</TableCell>
                    <TableCell align="right">Erstattet</TableCell>
                    <TableCell align="right">Auszuzahlen (brutto)</TableCell>
                    <TableCell>Auszahlung an</TableCell>
                    <TableCell align="right">Im Fahrzeug kassiert</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operatorPayoutRows.map(entry => (
                    <TableRow key={entry.operatorId}>
                      <TableCell>{entry.operatorName}</TableCell>
                      <TableCell align="right">{entry.appPaidCount}</TableCell>
                      <TableCell align="right">{formatMoney(entry.appPaidGross)}</TableCell>
                      <TableCell align="right">{entry.appPaidRefunded > 0 ? formatMoney(entry.appPaidRefunded) : '-'}</TableCell>
                      <TableCell align="right">
                        <Typography component="span" fontWeight={700} color={entry.payoutDue > 0 ? 'warning.main' : 'text.primary'}>
                          {formatMoney(entry.payoutDue)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {entry.payoutMethod === 'none' ? (
                          <Chip size="small" variant="outlined" color="default" label="nicht hinterlegt" />
                        ) : (
                          <Stack spacing={0.25}>
                            <Chip
                              size="small"
                              color={entry.payoutMethod === 'paypal' ? 'primary' : 'default'}
                              label={entry.payoutMethod === 'paypal' ? 'PayPal' : 'Bank'}
                              sx={{ width: 'fit-content' }}
                            />
                            <Typography variant="caption" color="text.secondary">{entry.payoutTarget}</Typography>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">{entry.inVehicleGross > 0 ? formatMoney(entry.inVehicleGross) : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Auftraggeber</TableCell>
                <TableCell>Ausführer</TableCell>
                <TableCell>Fahrer</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>Ziel</TableCell>
                <TableCell align="right">Betrag</TableCell>
                <TableCell>Zahlung</TableCell>
                <TableCell>Art</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {operatorReportRows.map(ride => {
                return (
                  <TableRow key={ride.id}>
                    <TableCell>{formatDateTime(ride.refDate)}</TableCell>
                    <TableCell>{ride.createdOperatorName}</TableCell>
                    <TableCell>{ride.driverOperatorName}</TableCell>
                    <TableCell>{ride.driverName || '-'}</TableCell>
                    <TableCell>{ride.start || '-'}</TableCell>
                    <TableCell>{ride.destination || '-'}</TableCell>
                    <TableCell align="right">{formatMoney(ride.billableFare)}</TableCell>
                    <TableCell>
                      <PaymentChip category={ride.paymentCategory} showInVehicle />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={ride.mediationType === 'cross_operator' ? 'warning' : ride.mediationType === 'central' ? 'primary' : 'default'}
                        label={getMediationLabel(ride.mediationType)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {operatorReportRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">Keine abgeschlossenen Unternehmer-Fahrten im Zeitraum</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };
  
  // Hauptinhalt basierend auf dem ausgewählten Tab
  const tabContent = useMemo(() => {
    switch (currentTab) {
      case TAB_OVERVIEW:  return renderOverviewTab;
      case TAB_RIDES:     return renderRidesTab();
      case TAB_DRIVERS:   return renderDriversTab();
      case TAB_WORK_TIMES: return renderWorkTimesTab();
      case TAB_REVENUE:   return renderRevenueTab();
      case TAB_OPERATORS: return renderOperatorsTab();
      case TAB_RECEIPTS:  return renderReceiptsTab();
      default:            return renderOverviewTab;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTab,
    renderOverviewTab,
    driverReportRows,
    operatorReportRows,
    operatorPayoutRows,
    revenueReportRows,
    revenueSummary,
    filteredWorkTimeRows,
    workTimeSummary,
    workTimeLoading,
    workTimeError,
    selectedDriverId,
    selectedCreatedOperatorId,
    selectedDriverOperatorId,
    selectedMediationStatus,
    selectedWorkTimeDriverId,
    selectedWorkTimeStatus,
    selectedWorkTimeBreakType,
    workTimeDriverOptions,
    workTimeBreakTypes,
    selectedWorkTimeRow,
    breakDialogOpen,
    editingBreak,
    breakForm,
    breakActionError,
    breakSaving,
    shiftDialogOpen,
    shiftForm,
    shiftActionError,
    shiftSaving,
    filteredReceiptRows,
    receiptSummary,
    receiptsLoading,
    receiptsError,
    receiptStatusFilter,
    receiptOpeningId,
    resendReceipt,
    resendEmail,
    resendError,
    resendBusy
  ]);
  
  return (
    <Container maxWidth="xl">
      <Paper elevation={2} sx={{ mt: 2, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h1">
            Berichte und Analysen: {dateRangeTitle}
          </Typography>
          
          <Box>
            <IconButton onClick={handleFilterClick} title="Zeitraum auswählen">
              <FilterIcon />
            </IconButton>
            <IconButton onClick={handlePrint} title="Drucken">
              <PrintIcon />
            </IconButton>
            <IconButton onClick={handleExportCsv} title="Exportieren">
              <DownloadIcon />
            </IconButton>
            
            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={handleFilterClose}
            >
              <MenuItem onClick={() => handleFilterSelect(FILTER_DAY)}>Heute</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_WEEK)}>Diese Woche</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_MONTH)}>Dieser Monat</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_LAST_MONTH)}>Letzter Monat</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_QUARTER)}>Dieses Quartal</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_YEAR)}>Dieses Jahr</MenuItem>
              <Divider />
              <MenuItem onClick={() => handleFilterSelect(FILTER_CUSTOM)}>Eigener Zeitraum</MenuItem>
            </Menu>
          </Box>
        </Box>

        <Dialog open={customRangeDialogOpen} onClose={handleCloseCustomRangeDialog} fullWidth maxWidth="xs">
          <DialogTitle>Eigener Zeitraum</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Von"
                type="date"
                value={customRangeDraftStart}
                onChange={(event) => setCustomRangeDraftStart(event.target.value)}
                InputLabelProps={{ shrink: true }}
                error={customRangeDraftInvalid}
                fullWidth
              />
              <TextField
                label="Bis"
                type="date"
                value={customRangeDraftEnd}
                onChange={(event) => setCustomRangeDraftEnd(event.target.value)}
                InputLabelProps={{ shrink: true }}
                error={customRangeDraftInvalid}
                helperText={customRangeDraftInvalid ? 'Bitte einen gültigen Zeitraum wählen.' : ''}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCustomRangeDialog}>Abbrechen</Button>
            <Button
              variant="contained"
              onClick={handleApplyCustomRange}
              disabled={customRangeDraftInvalid}
            >
              Anwenden
            </Button>
          </DialogActions>
        </Dialog>
        
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ mb: 3 }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Übersicht" />
            <Tab label="Fahrtenanalyse" />
            <Tab label="Fahrerleistung" />
            <Tab label="Arbeitszeiten" />
            <Tab label="Umsatzanalyse" />
            <Tab label="Unternehmer" />
            <Tab label="Quittungen" />
          </Tabs>
        </Box>
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <Typography variant="body1">Daten werden geladen...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ mt: 2 }}>
            <Typography color="error">
              Fehler beim Laden der Daten: {error}
            </Typography>
            <Button 
              variant="contained" 
              sx={{ mt: 2 }} 
              onClick={() => setDateRange(dateRange)}
            >
              Erneut versuchen
            </Button>
          </Box>
        ) : (
          tabContent
        )}
      </Paper>
    </Container>
  );
};

export default ReportsPage;
